import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ALLOWED_STATE_QUERY, DOCUMENT_STATE_REASONS_QUERY, DOCUMENT_STATES_LIST_QUERY } from './queries';
import { DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY } from '../list/queries';
import { SAVE_DOCUMENT_TYPE_ALLOWED_STATE } from './mutations';
import { DOCUMENT_TYPE_ALLOWED_STATE_SAVED } from './subscriptions';
import AllowedStateComponent from './AllowedStateComponent';
import { useValidation } from '@totalsoft/pure-validations-react';
import { allowedStateValidator, allowedStateReasonValidator } from 'features/documentTypes/validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { isValid } from '@totalsoft/pure-validations-react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Card, FakeText } from '@totalsoft/rocket-ui';
import { useToast } from 'hooks/toasts/useToast';

const AllowedStateContainer = ({ allowedStateId, onChangedAllowedStateTitle, onSaveFnChanged, documentTypeId, onRedirect }) => {
  const [stateEdit, setStateEdit] = useState({ changed: false, documentStateReasonsChanged: false });
  const [reasonsEdit, setReasonsEdit] = useState([]);
  const [disabledSave, setDisabledSave] = useState(true);
  const [allowedStateValidation, validateAllowedState] = useValidation(allowedStateValidator);
  const [allowedStateReasonValidation, validateAllowedStateReason] = useValidation(allowedStateReasonValidator);
  const [documentStateList, setDocumentStateList] = useState([]);

  const addToast = useToast();
  const { t } = useTranslation();
  const client = useApolloClient();

  const { loading } = useQuery(ALLOWED_STATE_QUERY, {
    onCompleted: data => {
      setStateEdit({
        ...data.allowedState,
        documentTypeId: documentTypeId,
        changed: false,
        documentStateReasonsChanged: false
      });
      if (data?.allowedState?.reasons) setReasonsEdit([...data.allowedState.reasons]);
    },
    variables: {
      id: allowedStateId
    },
    notifyOnNetworkStatusChange: true,
    skip: !allowedStateId
  });

  const { data: documentStateReasonsListData } = useQuery(DOCUMENT_STATE_REASONS_QUERY);

  const { data: documentStateListData } = useQuery(DOCUMENT_STATES_LIST_QUERY);

  const { data: documentTypeWithAllowedStatesData } = useQuery(DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY, {
    variables: {
      id: documentTypeId
    },
    notifyOnNetworkStatusChange: true
  });

  const [saveDocumentTypeAllowedState] = useMutation(SAVE_DOCUMENT_TYPE_ALLOWED_STATE, {
    onError: () => {
      setDisabledSave(false);
    }
  });

  useSubscription(DOCUMENT_TYPE_ALLOWED_STATE_SAVED, {
    onData: data => {
      addToast(t('DocumentTypes.DocumentAllowedState.DocumentTypeAllowedStateSaved'), 'success');
      if (!allowedStateId) onRedirect(data.data.data.documentTypeAllowedStateSaved.id);
      else {
        client.cache.evict({ id: `AllowedState:${allowedStateId}` });
        client.cache.evict({ id: `DocumentTypeWithAllowedStates:${documentTypeId}` });
      }
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'documentTypeWithAllowedStates' });
      client.cache.gc();
    }
  });

  const onStatePropertyChanged = useCallback(
    (propName, value) => {
      setStateEdit({ ...stateEdit, [propName]: value, changed: true });
      setDisabledSave(false);
    },
    [stateEdit]
  );

  const onReasonsPropertyChanged = useCallback(
    (index, propertyName, value) => {
      setReasonsEdit(
        Object.assign([...reasonsEdit], {
          [index]: {
            ...reasonsEdit[index],
            [propertyName]: value,
            documentStateId: stateEdit.documentStateId,
            documentTypeId: stateEdit.documentTypeId,
            requestReason: stateEdit.requestReason
          }
        })
      );
      setStateEdit({ ...stateEdit, documentStateReasonsChanged: true });
      setDisabledSave(false);
    },
    [reasonsEdit, stateEdit]
  );

  const onReasonsListChanged = useCallback(
    (eventType, index) => {
      const newReasons = [...reasonsEdit];
      if (eventType === 'AddItem') newReasons.push({});
      else if (eventType === 'DeleteItem') newReasons.splice(index, 1);

      setStateEdit({ ...stateEdit, documentStateReasonsChanged: true });
      setReasonsEdit(newReasons);
      setDisabledSave(false);
    },
    [reasonsEdit, stateEdit]
  );

  const onSave = useCallback(() => {
    if (!validateAllowedState(stateEdit) || !validateAllowedStateReason(reasonsEdit)) return;
    const allowedStateForSave = Object.assign({}, stateEdit);
    const reasonsForSave = reasonsEdit.map(x => {
      return {
        id: x.id,
        documentStateId: stateEdit.documentStateId,
        documentTypeId: documentTypeId,
        reasonId: x.reasonId,
        requestReason: stateEdit.requestReason ? stateEdit.requestReason : false
      };
    });
    allowedStateForSave.requestComment = stateEdit.requestComment ? stateEdit.requestComment : false;
    allowedStateForSave.commentIsMandatory = stateEdit.commentIsMandatory ? stateEdit.commentIsMandatory : false;
    saveDocumentTypeAllowedState({
      variables: {
        allowedState: {
          id: allowedStateForSave.id,
          documentTypeId: documentTypeId,
          documentStateId: allowedStateForSave.documentStateId,
          documentColor: allowedStateForSave.documentColor,
          requestComment: allowedStateForSave.requestComment,
          commentIsMandatory: allowedStateForSave.commentIsMandatory,
          reasons: reasonsForSave
        }
      }
    });
    setDisabledSave(true);
    setStateEdit({ ...allowedStateForSave, changed: false, documentStateReasonsChanged: false });
    setReasonsEdit(reasonsForSave);
  }, [reasonsEdit, stateEdit, documentTypeId, saveDocumentTypeAllowedState, validateAllowedState, validateAllowedStateReason]);

  useEffect(() => {
    const allowedStateTitle = {
      documentTypeName: stateEdit.documentTypeName || '',
      allowedStateName: stateEdit.documentStateName || ''
    };
    onChangedAllowedStateTitle(allowedStateTitle);
  }, [stateEdit, onChangedAllowedStateTitle, stateEdit.documentStateId]);

  useEffect(() => {
    onSaveFnChanged(onSave, disabledSave || !(allowedStateValidation |> isValid) || !(allowedStateReasonValidation |> isValid));
  }, [onSave, onSaveFnChanged, allowedStateValidation, allowedStateReasonValidation, disabledSave]);

  useEffect(() => {
    validateAllowedState(stateEdit);
    validateAllowedStateReason(reasonsEdit);
  }, [stateEdit, reasonsEdit, validateAllowedState, validateAllowedStateReason]);

  useEffect(() => {
    setDocumentStateList(
      documentStateListData?.documentStatesList.filter(
        state =>
          state.id === stateEdit.documentStateId ||
          !documentTypeWithAllowedStatesData?.documentTypeWithAllowedStates?.allowedStates.some(item => state.id === item.documentStateId)
      )
    );
  }, [
    documentTypeWithAllowedStatesData?.documentTypeWithAllowedStates?.allowedStates,
    documentStateListData?.documentStatesList,
    stateEdit.documentStateId
  ]);

  return (
    <>
      {loading || (allowedStateId && !stateEdit.id) ? (
        <Card>
          <FakeText lines={10} />
        </Card>
      ) : (
        <AllowedStateComponent
          stateEdit={stateEdit}
          reasonsEdit={reasonsEdit}
          documentStateList={documentStateList || []}
          documentStateReasonsList={documentStateReasonsListData?.documentStateReasonsList || []}
          onStatePropertyChanged={onStatePropertyChanged}
          onReasonsPropertyChanged={onReasonsPropertyChanged}
          onReasonsListChanged={onReasonsListChanged}
          disabledSave={disabledSave}
          onSave={onSave}
          validation={allowedStateValidation}
          reasonValidation={allowedStateReasonValidation}
        />
      )}
    </>
  );
};

AllowedStateContainer.propTypes = {
  documentTypeId: PropTypes.number.isRequired,
  allowedStateId: PropTypes.number,
  onChangedAllowedStateTitle: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired
};

export default AllowedStateContainer;
